import React, { MouseEvent, useEffect, useMemo, useState, useRef } from 'react'
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  Box,
  Typography,
  ToggleButton,
  Snackbar,
  Alert,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import dayjs from 'dayjs'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { tableRowStyle } from 'styles/tableRowStyle'
import { useBin } from 'hooks/useBin'
import { useTask } from 'hooks/useTask'

import BinInventoryPopover from 'components/BinInventoryPopover'
import ProductPopover from 'components/ProductPopover'
import OutOfStockSourceBins from './OutOfStockSourceBins'
import { isRushTaskNote } from 'types/task'

interface TaskTableProps {
  tasks: any[]
  isLoading: boolean
  page: number
  rowsPerPage: number
  onPageChange: (event: unknown, newPage: number) => void
  onCancel: (taskID: string) => void
  onRefresh: () => void
  serverPaginated?: boolean
  totalCount?: number
  /** Pending / In Process / Out of Stock：显示加急列并可 PATCH note */
  rushControlsEnabled?: boolean
  onApproveCancelRequest: (taskID: string) => Promise<void> | void
}

type BinEntry = {
  code: string
  qty: number
  inventoryID: string
}

const ROW_DEFAULT_BG = '#ffffff'
const ROW_STRIPE_BG = '#fafafa'

const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  isLoading,
  page,
  rowsPerPage,
  onPageChange,
  onRefresh,
  serverPaginated = false,
  totalCount,
  rushControlsEnabled = false,
  onApproveCancelRequest
}) => {
  const [editTaskID, setEditTaskID] = useState<string | null>(null)
  const [editedStatus, setEditedStatus] = useState('')
  const [editedSourceBinCode, setEditedSourceBinCode] = useState<string>('')
  const [editedSourceInventoryID, setEditedSourceInventoryID] =
    useState<string>('')
  const [snackOpen, setSnackOpen] = useState(false)
  const [rushSnack, setRushSnack] = useState<{
    open: boolean
    msg: string
    sev: 'success' | 'error'
  }>({ open: false, msg: '', sev: 'error' })
  const [rushNoteOverrideByTaskId, setRushNoteOverrideByTaskId] = useState<
    Record<string, string | null>
  >({})
  const [rushUpdatingTaskId, setRushUpdatingTaskId] = useState<string | null>(
    null
  )
  const [cancelDecisionTask, setCancelDecisionTask] = useState<{
    task: any
    action: 'approve' | 'decline'
  } | null>(null)
  const [cancelDecisionLoading, setCancelDecisionLoading] = useState(false)

  const { fetchBinCodes } = useBin()
  const { updateTask, declineCancelRequest } = useTask()

  const [pageSwitching, setPageSwitching] = useState(false)
  useEffect(() => {
    if (!isLoading && pageSwitching) setPageSwitching(false)
  }, [isLoading, pageSwitching])

  const [binPopoverOpen, setBinPopoverOpen] = useState(false)
  const [binPopoverAnchor, setBinPopoverAnchor] = useState<HTMLElement | null>(
    null
  )
  const [binPopoverCode, setBinPopoverCode] = useState<string | null>(null)

  const dragRef = useRef<HTMLDivElement | null>(null)
  const isDraggingRef = useRef(false)
  const startXRef = useRef(0)
  const scrollLeftRef = useRef(0)

  const openBinPopover = (
    evt: MouseEvent<HTMLElement>,
    code?: string | null
  ) => {
    if (!code) return
    setBinPopoverAnchor(evt.currentTarget)
    setBinPopoverCode(code)
    setBinPopoverOpen(true)
  }
  const closeBinPopover = () => {
    setBinPopoverOpen(false)
    setBinPopoverAnchor(null)
    setBinPopoverCode(null)
  }

  const [prodPopoverOpen, setProdPopoverOpen] = useState(false)
  const [prodPopoverAnchor, setProdPopoverAnchor] =
    useState<HTMLElement | null>(null)
  const [prodCode, setProdCode] = useState<string | null>(null)

  const openProductPopover = (
    evt: MouseEvent<HTMLElement>,
    code?: string | null
  ) => {
    if (!code) return
    setProdPopoverAnchor(evt.currentTarget)
    setProdCode(code)
    setProdPopoverOpen(true)
  }
  const closeProductPopover = () => {
    setProdPopoverOpen(false)
    setProdPopoverAnchor(null)
    setProdCode(null)
  }

  const mergedTasks = useMemo(() => {
    if (!rushControlsEnabled || !Object.keys(rushNoteOverrideByTaskId).length) {
      return tasks
    }
    return tasks.map((t: any) => {
      const tid = t?.taskID != null && t?.taskID !== '' ? String(t.taskID) : ''
      if (!tid || !(tid in rushNoteOverrideByTaskId)) return t
      const ovr = rushNoteOverrideByTaskId[tid]
      return { ...t, note: ovr }
    })
  }, [tasks, rushControlsEnabled, rushNoteOverrideByTaskId])

  useEffect(() => {
    if (!rushControlsEnabled || !tasks.length) return
    setRushNoteOverrideByTaskId(prev => {
      const next = { ...prev }
      let dirty = false
      for (const tid of Object.keys(next)) {
        const row = tasks.find((x: any) => String(x?.taskID || '') === tid)
        if (!row) {
          delete next[tid]
          dirty = true
          continue
        }
        const srv = (row?.note ?? null) as string | null
        const ovr = next[tid]
        if (isRushTaskNote(srv) === isRushTaskNote(ovr)) {
          delete next[tid]
          dirty = true
        }
      }
      return dirty ? next : prev
    })
  }, [tasks, rushControlsEnabled])

  const visibleRows = serverPaginated
    ? mergedTasks
    : mergedTasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const handleLocalPageChange = (e: unknown, newPage: number) => {
    if (serverPaginated) setPageSwitching(true)
    onPageChange(e, newPage)
  }

  const count = serverPaginated ? totalCount ?? 0 : mergedTasks.length

  const colCount = rushControlsEnabled ? 11 : 10

  const handleRushToggle = async (task: any, checked: boolean) => {
    const tid = String(task?.taskID || '')
    if (!tid || !rushControlsEnabled) return
    const cur =
      tid in rushNoteOverrideByTaskId
        ? rushNoteOverrideByTaskId[tid]
        : (task?.note ?? null)
    if (isRushTaskNote(cur) === checked) return
    if (rushUpdatingTaskId) return

    setRushNoteOverrideByTaskId(prev => ({
      ...prev,
      [tid]: checked ? 'RUSH_TASK' : null
    }))
    try {
      setRushUpdatingTaskId(tid)
      const res = await updateTask(tid, {
        note: checked ? 'RUSH_TASK' : null
      })
      if (!res?.success) {
        throw new Error((res as any)?.message || 'Failed to update rush.')
      }
      setRushSnack({
        open: true,
        msg: checked ? 'Marked as rush.' : 'Rush removed.',
        sev: 'success'
      })
      onRefresh()
    } catch (e: any) {
      setRushNoteOverrideByTaskId(prev => {
        const n = { ...prev }
        delete n[tid]
        return n
      })
      setRushSnack({
        open: true,
        msg: e?.message || 'Failed to update rush.',
        sev: 'error'
      })
    } finally {
      setRushUpdatingTaskId(null)
    }
  }

  const handleCancelRequestDecision = async () => {
    if (!cancelDecisionTask?.task?.taskID) return
    const { task, action } = cancelDecisionTask

    try {
      setCancelDecisionLoading(true)
      if (action === 'approve') {
        await onApproveCancelRequest(task.taskID)
      } else {
        const res = await declineCancelRequest(task.taskID)
        if (!res.success) {
          throw new Error(res.message || 'Decline failed')
        }
        onRefresh()
      }
      setCancelDecisionTask(null)
    } catch (e: any) {
      setRushSnack({
        open: true,
        msg: e?.message || 'Failed to handle cancel request.',
        sev: 'error'
      })
    } finally {
      setCancelDecisionLoading(false)
    }
  }

  const cellStyle = {
    border: '1px solid #e0e0e0',
    whiteSpace: 'nowrap' as const,
    padding: '3px 8px',
    height: 10,
    verticalAlign: 'middle' as const,
    fontSize: 14
  }

  const SOURCEBIN_MIN_WIDTH = 250
  const SOURCEBIN_VIEW_WIDTH = 320

  const handleSave = async (task: any) => {
    let sourceBin = editedSourceBinCode
    const sourceBinCount = task.sourceBins?.length || 0
    const isOutOfStock = sourceBinCount === 0

    if (editedStatus === 'COMPLETED' && sourceBinCount > 0 && !sourceBin) {
      setSnackOpen(true)
      return
    }

    if (isOutOfStock) {
      if (editedStatus === 'COMPLETED') {
        sourceBin = 'Transfer-in'
      } else if (editedStatus === 'CANCELED') {
        sourceBin = 'Out of Stock'
      }
    }

    if (task.status === 'PENDING' && editedStatus === 'CANCELED') {
      if (sourceBinCount > 1) {
        sourceBin = 'Expired'
      } else if (sourceBinCount === 1) {
        sourceBin = task.sourceBins[0]?.bin?.binCode || ''
      }
    }

    if (sourceBinCount === 1 && !sourceBin && editedStatus !== 'COMPLETED') {
      sourceBin = task.sourceBins[0]?.bin?.binCode || ''
    }

    await updateTask(
      task.taskID,
      {
        status: editedStatus,
        sourceBinCode: sourceBin
      },
      {
        warehouseID: task.warehouseID,
        status: task.status,
        keyword: ''
      }
    )

    setEditTaskID(null)
    setEditedSourceBinCode('')
    setEditedSourceInventoryID('')
    setEditedStatus('')
    onRefresh()
  }

  const headLabels = [
    'Product',
    'Qty',
    'Source Bin',
    'Target Bin',
    'Created / Updated',
    'Status',
    'Cancel Request',
    'Creator',
    'Accepter',
    ...(rushControlsEnabled ? ['Rush'] : []),
    'Action'
  ]

  const Head = (
    <TableHead>
      <TableRow>
        {headLabels.map(label => (
          <TableCell
            key={label}
            align='center'
            sx={{ ...cellStyle, fontWeight: 600, background: '#f0f4f9' }}
          >
            {label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )

  const renderRow = (task: any, idx: number) => {
    const isEditing = editTaskID === task.taskID
    const isOutOfStock = !task.sourceBins || task.sourceBins.length === 0
    const showEditableBin = isEditing && editedStatus === 'COMPLETED'
    const cancelRequestDecision = task.cancelRequestDecision as
      | 'APPROVED'
      | 'DECLINED'
      | null
      | undefined
    const hasPendingCancelRequest = Boolean(
      task.cancelRequestedAt && !cancelRequestDecision
    )
    const hasCancelRequestRecord = Boolean(
      task.cancelRequestedAt || cancelRequestDecision
    )
    const cancelRequesterName = task.cancelRequester
      ? `${task.cancelRequester.firstName || ''} ${
          task.cancelRequester.lastName || ''
        }`.trim()
      : ''

    const binEntries: BinEntry[] = (task.sourceBins || []).map(
      (s: any): BinEntry => ({
        code: s?.bin?.binCode ?? '',
        qty: Number(s?.quantity ?? 0),
        inventoryID: String(s?.inventoryID ?? '')
      })
    )

    const displayBinCodes: string[] = (task.sourceBins || []).map(
      (s: any) => s?.bin?.binCode
    )

    const tooManyBins = (task.sourceBins || []).length > 8

    const baseRowBg = idx % 2 === 0 ? ROW_DEFAULT_BG : ROW_STRIPE_BG
    const rowBg = isEditing
      ? '#e8f4fd'
      : hasPendingCancelRequest
        ? '#f8fafc'
        : baseRowBg

    return (
      <TableRow
        key={task.taskID}
        sx={{
          ...tableRowStyle,
          backgroundColor: rowBg,
          transition: 'background-color 140ms ease',
          '& td': { verticalAlign: 'middle' }
        }}
      >
        <TableCell align='center' sx={cellStyle}>
          <Typography
            component='span'
            fontSize={14}
            sx={{ cursor: 'pointer', color: '#3F72AF' }}
            onClick={e => openProductPopover(e, task.productCode)}
            title='Click to view product'
          >
            {task.productCode}
          </Typography>
        </TableCell>

        <TableCell align='center' sx={cellStyle}>
          {task.quantity === 0 ? 'ALL' : task.quantity ?? '--'}
        </TableCell>

        {/* Source Bin */}
        <TableCell
          align='center'
          sx={{
            ...cellStyle,
            minWidth: SOURCEBIN_MIN_WIDTH,
            width: SOURCEBIN_VIEW_WIDTH,
            maxWidth: SOURCEBIN_VIEW_WIDTH,
            overflow: 'hidden'
          }}
        >
          {showEditableBin ? (
            <Box display='flex' justifyContent='center' flexWrap='wrap' gap={1}>
              {binEntries.map((entry: BinEntry) => {
                const selected = editedSourceInventoryID === entry.inventoryID
                return (
                  <ToggleButton
                    key={entry.inventoryID}
                    value={entry.inventoryID}
                    selected={selected}
                    onClick={() => {
                      setEditedSourceInventoryID(entry.inventoryID)
                      setEditedSourceBinCode(entry.code)
                    }}
                    sx={{
                      minWidth: 100,
                      color: selected ? 'success.main' : '#666',
                      borderColor: selected ? 'success.main' : '#ccc',
                      backgroundColor: selected ? '#e8f5e9' : '#f5f5f5',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {selected ? '✔ ' : ''}
                    {entry.code} ({entry.qty})
                  </ToggleButton>
                )
              })}
            </Box>
          ) : isOutOfStock ? (
            <OutOfStockSourceBins otherInventories={task.otherInventories} />
          ) : (
            <>
              {tooManyBins ? (
                <Box
                  ref={dragRef}
                  onMouseDown={e => {
                    isDraggingRef.current = true
                    startXRef.current = e.pageX
                    scrollLeftRef.current = e.currentTarget.scrollLeft
                    e.currentTarget.style.cursor = 'grabbing'
                  }}
                  onMouseLeave={e => {
                    isDraggingRef.current = false
                    e.currentTarget.style.cursor = 'grab'
                  }}
                  onMouseUp={e => {
                    isDraggingRef.current = false
                    e.currentTarget.style.cursor = 'grab'
                  }}
                  onMouseMove={e => {
                    if (!isDraggingRef.current) return
                    const x = e.pageX - startXRef.current
                    e.currentTarget.scrollLeft = scrollLeftRef.current - x
                  }}
                  sx={{
                    display: 'block',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    textAlign: 'left',
                    px: 1,
                    cursor: 'grab',
                    userSelect: 'none',
                    // Hide scrollbars completely:
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE/Edge
                    '&::-webkit-scrollbar': {
                      display: 'none' // Chrome/Safari
                    }
                  }}
                >
                  <Box component='span' sx={{ display: 'inline-flex', gap: 8 }}>
                    {displayBinCodes.map((code: string, i: number) => (
                      <Typography
                        key={`${code}-${i}`}
                        fontSize={14}
                        sx={{
                          cursor: 'pointer',
                          color: '#3F72AF',
                          display: 'inline-block'
                        }}
                        onClick={e => openBinPopover(e, code)}
                        title='Click to view bin inventory'
                      >
                        {code}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  display='flex'
                  flexWrap='wrap'
                  justifyContent='center'
                  gap={1}
                >
                  {displayBinCodes.map((code: string, i: number) => (
                    <Typography
                      key={`${code}-${i}`}
                      fontSize={14}
                      sx={{ cursor: 'pointer', color: '#3F72AF' }}
                      onClick={e => openBinPopover(e, code)}
                      title='Click to view bin inventory'
                    >
                      {code}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </TableCell>

        {/* Target Bin */}
        <TableCell align='center' sx={cellStyle}>
          {task.destinationBinCode ? (
            <Typography
              fontSize={14}
              sx={{ cursor: 'pointer', color: '#3F72AF' }}
              onClick={e => openBinPopover(e, task.destinationBinCode)}
              title='Click to view bin inventory'
            >
              {task.destinationBinCode}
            </Typography>
          ) : (
            '--'
          )}
        </TableCell>

        {/* Created / Updated */}
        <TableCell align='center' sx={cellStyle}>
          <Typography variant='caption' sx={{ display: 'block' }}>
            {dayjs(task.createdAt).format('YYYY-MM-DD HH:mm')}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {dayjs(task.updatedAt).format('YYYY-MM-DD HH:mm')}
          </Typography>
        </TableCell>

        {/* Status */}
        <TableCell align='center' sx={cellStyle}>
          {isEditing ? (
            <Select
              value={editedStatus}
              onChange={e => setEditedStatus(e.target.value)}
              size='small'
              sx={{ minWidth: 120 }}
            >
              <MenuItem value='PENDING'>PENDING</MenuItem>
              <MenuItem value='COMPLETED'>COMPLETED</MenuItem>
              <MenuItem value='CANCELED'>CANCELED</MenuItem>
            </Select>
          ) : (
            <Typography>{task.status}</Typography>
          )}
        </TableCell>

        <TableCell align='center' sx={cellStyle}>
          {hasCancelRequestRecord ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ lineHeight: 1.2, fontWeight: 700 }}
              >
                {cancelRequesterName || task.cancelRequestedBy || 'Unknown'}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ lineHeight: 1.2 }}
              >
                {dayjs(task.cancelRequestedAt).format('YYYY-MM-DD HH:mm')}
              </Typography>
              {hasPendingCancelRequest ? (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    mt: 0.5,
                    justifyContent: 'center'
                  }}
                >
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() =>
                      setCancelDecisionTask({ task, action: 'approve' })
                    }
                    sx={{
                      minWidth: 0,
                      px: 1,
                      py: 0.1,
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#15803d',
                      borderColor: '#86efac',
                      bgcolor: '#f0fdf4',
                      '&:hover': {
                        borderColor: '#4ade80',
                        bgcolor: '#dcfce7'
                      }
                    }}
                  >
                    Approve
                  </Button>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() =>
                      setCancelDecisionTask({ task, action: 'decline' })
                    }
                    sx={{
                      minWidth: 0,
                      px: 1,
                      py: 0.1,
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#1d4ed8',
                      borderColor: '#93c5fd',
                      bgcolor: '#eff6ff',
                      '&:hover': {
                        borderColor: '#60a5fa',
                        bgcolor: '#dbeafe'
                      }
                    }}
                  >
                    Decline
                  </Button>
                </Box>
              ) : (
                <Typography
                  variant='caption'
                  sx={{
                    mt: 0.5,
                    lineHeight: 1.2,
                    fontWeight: 800,
                    color:
                      cancelRequestDecision === 'APPROVED'
                        ? 'success.main'
                        : 'error.main'
                  }}
                >
                  {cancelRequestDecision === 'APPROVED'
                    ? 'Approved'
                    : 'Declined'}
                </Typography>
              )}
            </Box>
          ) : (
            '--'
          )}
        </TableCell>

        <TableCell align='center' sx={cellStyle}>
          {task.creator
            ? `${task.creator.firstName || ''} ${
                task.creator.lastName || ''
              }`.trim()
            : 'TBD'}
        </TableCell>

        <TableCell align='center' sx={cellStyle}>
          {task.accepter
            ? `${task.accepter.firstName || ''} ${
                task.accepter.lastName || ''
              }`.trim()
            : 'TBD'}
        </TableCell>

        {rushControlsEnabled ? (
          <TableCell align='center' sx={cellStyle}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                minHeight: 32
              }}
            >
              {rushUpdatingTaskId === String(task.taskID) ? (
                <CircularProgress size={18} sx={{ color: 'error.main' }} />
              ) : null}
              <Switch
                size='small'
                color='error'
                checked={isRushTaskNote(
                  String(task.taskID) in rushNoteOverrideByTaskId
                    ? rushNoteOverrideByTaskId[String(task.taskID)]
                    : (task.note ?? null)
                )}
                onChange={(_, c) => handleRushToggle(task, c)}
                disabled={Boolean(rushUpdatingTaskId)}
                inputProps={{ 'aria-label': 'Rush task' }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#d32f2f'
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#ef9a9a'
                  }
                }}
              />
            </Box>
          </TableCell>
        ) : null}

        {/* Action */}
        <TableCell align='center' sx={cellStyle}>
          {isEditing ? (
            <>
              <IconButton
                onClick={() => handleSave(task)}
                size='small'
                sx={{ color: 'success.main' }}
              >
                <SaveIcon fontSize='small' />
              </IconButton>
              <IconButton
                onClick={() => {
                  setEditTaskID(null)
                  setEditedSourceBinCode('')
                  setEditedSourceInventoryID('')
                  setEditedStatus('')
                }}
                size='small'
                sx={{ color: 'purple' }}
              >
                <CancelIcon fontSize='small' />
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={() => {
                if (task.status !== 'PENDING') return
                fetchBinCodes()
                setEditedStatus(task.status)
                const first = (task.sourceBins || [])[0]
                setEditedSourceBinCode(first?.bin?.binCode || '')
                setEditedSourceInventoryID(first?.inventoryID || '')
                setEditTaskID(task.taskID)
              }}
              size='small'
              sx={{ color: task.status === 'PENDING' ? '#3F72AF' : '#ccc' }}
              disabled={task.status !== 'PENDING'}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    )
  }

  const overlayActive = serverPaginated && (isLoading || pageSwitching)
  const inlineLoading = !serverPaginated && isLoading

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          boxShadow: 2,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Table>
            {Head}
            <TableBody>
              {inlineLoading ? (
                <TableRow>
                  <TableCell colSpan={colCount} align='center' sx={cellStyle}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 && !overlayActive ? (
                <TableRow>
                  <TableCell colSpan={colCount} align='center' sx={cellStyle}>
                    No data
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((task, idx) => renderRow(task, idx))
              )}
            </TableBody>
          </Table>

          {overlayActive && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(15, 23, 42, 0.04)',
                transition: 'opacity 150ms ease',
                zIndex: 2
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>

        <TablePagination
          component='div'
          count={count}
          page={page}
          onPageChange={handleLocalPageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          sx={{
            minHeight: 36,
            '.MuiTablePagination-toolbar': { minHeight: 36, height: 36 },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows':
              {
                margin: 0,
                fontSize: 12,
                lineHeight: 1.2
              },
            '.MuiInputBase-root': { height: 28, fontSize: 12 },
            '.MuiTablePagination-actions': { margin: 0 }
          }}
        />
      </Paper>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message='Please select a source bin before confirming COMPLETED'
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Snackbar
        open={rushSnack.open}
        autoHideDuration={2600}
        onClose={() => setRushSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setRushSnack(s => ({ ...s, open: false }))}
          severity={rushSnack.sev}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {rushSnack.msg}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(cancelDecisionTask)}
        onClose={() => {
          if (!cancelDecisionLoading) setCancelDecisionTask(null)
        }}
        maxWidth='xs'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <Box
          sx={{
            height: 5,
            bgcolor:
              cancelDecisionTask?.action === 'approve'
                ? '#bbf7d0'
                : '#bfdbfe'
          }}
        />
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
          {cancelDecisionTask?.action === 'approve'
            ? 'Approve cancellation'
            : 'Decline cancellation'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography color='text.secondary' fontSize={14} mb={2}>
            {cancelDecisionTask?.action === 'approve'
              ? 'The task will move to Canceled and disappear from Pending.'
              : 'The task will return to the normal pending queue for workers.'}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '88px 1fr',
              rowGap: 0.75,
              columnGap: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: '#f8fafc',
              border: '1px solid #e5e7eb'
            }}
          >
            <Typography variant='caption' color='text.secondary'>
              Product
            </Typography>
            <Typography variant='caption' fontWeight={800}>
              {cancelDecisionTask?.task?.productCode || '--'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Requested by
            </Typography>
            <Typography variant='caption' fontWeight={700}>
              {cancelDecisionTask?.task?.cancelRequester
                ? `${cancelDecisionTask.task.cancelRequester.firstName || ''} ${
                    cancelDecisionTask.task.cancelRequester.lastName || ''
                  }`.trim()
                : cancelDecisionTask?.task?.cancelRequestedBy || 'Unknown'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Requested at
            </Typography>
            <Typography variant='caption'>
              {cancelDecisionTask?.task?.cancelRequestedAt
                ? dayjs(cancelDecisionTask.task.cancelRequestedAt).format(
                    'YYYY-MM-DD HH:mm'
                  )
                : '--'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant='text'
            color='inherit'
            onClick={() => setCancelDecisionTask(null)}
            disabled={cancelDecisionLoading}
            sx={{ fontWeight: 700 }}
          >
            Close
          </Button>
          <Button
            variant='outlined'
            onClick={handleCancelRequestDecision}
            disabled={cancelDecisionLoading}
            sx={{
              fontWeight: 800,
              color:
                cancelDecisionTask?.action === 'approve'
                  ? '#15803d'
                  : '#1d4ed8',
              borderColor:
                cancelDecisionTask?.action === 'approve'
                  ? '#86efac'
                  : '#93c5fd',
              bgcolor:
                cancelDecisionTask?.action === 'approve'
                  ? '#f0fdf4'
                  : '#eff6ff',
              '&:hover': {
                borderColor:
                  cancelDecisionTask?.action === 'approve'
                    ? '#4ade80'
                    : '#60a5fa',
                bgcolor:
                  cancelDecisionTask?.action === 'approve'
                    ? '#dcfce7'
                    : '#dbeafe'
              }
            }}
          >
            {cancelDecisionLoading
              ? 'Working...'
              : cancelDecisionTask?.action === 'approve'
                ? 'Approve'
                : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>

      <BinInventoryPopover
        open={binPopoverOpen}
        anchorEl={binPopoverAnchor}
        binCode={binPopoverCode}
        onClose={closeBinPopover}
      />

      <ProductPopover
        open={prodPopoverOpen}
        anchorEl={prodPopoverAnchor}
        productCode={prodCode}
        onClose={closeProductPopover}
      />
    </>
  )
}

export default TaskTable
